/**
 * Centralized Enum Constants
 * 
 * Single source of truth for all enum values used across the application.
 * Convention: Database uses lowercase_snake_case, UI displays Title Case labels.
 * 
 * IMPORTANT: These values must match exactly with:
 * - Backend database enum values
 * - MCP server normalization functions
 * - Field validators in Pydantic schemas
 */

// Company
export const COMPANY_STATUSES = [
  { value: 'suspect', label: 'Suspect' },
  { value: 'target', label: 'Target' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'customer', label: 'Customer' },
  { value: 'former_customer', label: 'Former Customer' },
  { value: 'lost', label: 'Lost' }
] as const;

// Proposition
export const PROPOSITION_TYPES = [
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
  { value: 'solution', label: 'Solution' },
  { value: 'capability', label: 'Capability' }
] as const;

export const CURRENT_CAPABILITIES = [
  { value: 'idea', label: 'Idea' },
  { value: 'concept', label: 'Concept' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'developing', label: 'Developing' },
  { value: 'proven', label: 'Proven' },
  { value: 'mature', label: 'Mature' },
  { value: 'retiring', label: 'Retiring' }
] as const;

export const PRICING_TYPES = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'one_time', label: 'One Time' },
  { value: 'usage_based', label: 'Usage Based' },
  { value: 'tiered', label: 'Tiered' },
  { value: 'custom', label: 'Custom' }
] as const;

export const PRICING_FREQUENCIES = [
  { value: 'one_time', label: 'One Time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'custom', label: 'Custom' }
] as const;

// Segment
export const FOCUS_MODES = [
  { value: 'focus', label: 'Focus' },
  { value: 'explore', label: 'Explore' },
  { value: 'opportunistic', label: 'Opportunistic' },
  { value: 'maintain', label: 'Maintain' },
  { value: 'exit', label: 'Exit' },
  { value: 'none', label: 'None' },
  { value: 'not_relevant', label: 'Not Relevant' }
] as const;

export const MARKET_MATURITIES = [
  { value: 'emerging', label: 'Emerging' },
  { value: 'growing', label: 'Growing' },
  { value: 'mature', label: 'Mature' },
  { value: 'declining', label: 'Declining' }
] as const;

export const SEGMENT_PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
] as const;

// Opportunity
export const OPPORTUNITY_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' }
] as const;

// Task (Master Plan)
export const TASK_STATUSES = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'blocked', label: 'Blocked' }
] as const;

export const TASK_PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
] as const;

// OKR
export const OKR_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
] as const;

export const OKR_COMMITMENT_TYPES = [
  { value: 'committed', label: 'Committed' },
  { value: 'aspirational', label: 'Aspirational' }
] as const;

export const KEY_RESULT_STATUSES = [
  { value: 'on-track', label: 'On Track' },
  { value: 'at-risk', label: 'At Risk' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'completed', label: 'Completed' }
] as const;

// Planning Cycle
export const PLANNING_CYCLE_TYPES = [
  { value: '30-day', label: '30 Day' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: '3-year', label: '3 Year' }
] as const;

// Research
export const RESEARCH_NOTE_TYPES = [
  { value: 'insight', label: 'Insight' },
  { value: 'research', label: 'Research' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'general', label: 'General' }
] as const;

export const RESEARCH_SOURCE_TYPES = [
  { value: 'manual', label: 'Manual' },
  { value: 'automated', label: 'Automated' }
] as const;

export const RESEARCH_VISIBILITIES = [
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' }
] as const;

// Account Plan
export const HYPOTHESIS_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'testing', label: 'Testing' },
  { value: 'validated', label: 'Validated' },
  { value: 'invalidated', label: 'Invalidated' }
] as const;

export const ACCOUNT_STAGES = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' }
] as const;

// Organization
export const ORGANIZATION_AREAS = [
  { value: 'product', label: 'Product' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operations', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
  { value: 'hr', label: 'HR' },
  { value: 'technology', label: 'Technology' },
  { value: 'other', label: 'Other' }
] as const;

// User
export const USER_ROLES = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' }
] as const;

export const USER_TYPE_IMPORTANCES = [
  { value: 'critical', label: 'Critical' },
  { value: 'important', label: 'Important' },
  { value: 'nice_to_have', label: 'Nice To Have' }
] as const;

export const ROLE_TYPES = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'decision_maker', label: 'Decision Maker' },
  { value: 'user', label: 'User' },
  { value: 'gatekeeper', label: 'Gatekeeper' },
  { value: 'champion', label: 'Champion' }
] as const;

// Helper functions to get values and labels
export function getEnumValues<T extends readonly { value: string; label: string }[]>(
  enumArray: T
): string[] {
  return enumArray.map(item => item.value);
}

export function getEnumLabels<T extends readonly { value: string; label: string }[]>(
  enumArray: T
): string[] {
  return enumArray.map(item => item.label);
}

export function getEnumLabel<T extends readonly { value: string; label: string }[]>(
  enumArray: T,
  value: string | null | undefined,
  defaultLabel = 'Not Set'
): string {
  if (!value) return defaultLabel;
  const item = enumArray.find(e => e.value === value);
  return item?.label || defaultLabel;
}